<template>
  <div>
    <h6>
      {{ question.label }} {{ question.required ? '*' : '' }}
      <small v-if="isLockable" class="text-muted">
        <i class="fa fa-fw fa-lock"></i>
      </small>
      <small v-if="isEditable">
        <b-button v-if="isEditable" variant="link" size="sm" class="text-danger py-0" @click="editing = !editing">
          {{ trans(!editing ? 'edit' : 'save') }}
        </b-button>
      </small>
    </h6>
    <div class="mb-2" v-html="question.description"></div>
    <b-form-group style="position: relative;">
      <b-form-input
        v-if="!textarea"
        :size="size"
        type="text"
        v-model="answer"
        :name="question.slug"
        :required="question.required"
        :state="!!isValid ? null : false"
        :placeholder="question.placeholder"
        :readonly="isLocked"
        @focus="$emit('clear-error', question.slug)"
      />
      <b-form-textarea
        v-if="textarea && !question.options.wysi"
        :size="size"
        type="text"
        v-model="answer"
        :name="question.slug"
        :required="question.required"
        :state="!!isValid ? null : false"
        :placeholder="question.placeholder"
        :readonly="isLocked"
        @focus="$emit('clear-error', question.slug)"
      />
      <ckeditor
        v-if="question.options.wysi"
        id="ckeditor"
        v-model="answer"
        :editor="editor"
        :config="editorConfig"
        :placeholder="question.placeholder"
        :readonly="isLocked"
      ></ckeditor>
      <form-input-question-word-count
        v-if="wordLimitActive"
        :word-count="wordCountNum"
        :word-limit="wordLimitNum"
        :word-limit-active="wordLimitActive"/>
      <b-form-invalid-feedback :state="!!errors ? false : null">
        {{ (errors || []).join(' ') }}
      </b-form-invalid-feedback>
    </b-form-group>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough'
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript'
import Font from '@ckeditor/ckeditor5-font/src/font'
import Link from '@ckeditor/ckeditor5-link/src/link'
import List from '@ckeditor/ckeditor5-list/src/list'
import Indent from '@ckeditor/ckeditor5-indent/src/indent'
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock'
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import Placeholder from '@ckeditor/ckeditor5-placeholder/src/placeholder'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import FormInputQuestionWordCount from "./FormInputQuestionWordCount.vue";

function deserialize(value, question) {
  return value === undefined ? question.options.default || '' : value || ''
}

export default {
  props: {
    organization: Object,
    value: String, // answer
    question: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    errors: Array,
    wordLimit: Number | String,
    wordLimitActive: Boolean,
  },
  components: {
    FormInputQuestionWordCount,
    ckeditor: CKEditor.component,
  },
  data() {
    return {
      trans: trans,
      answer: deserialize(this.value, this.question),
      editing: false,
      error: null,
      editor: ClassicEditor,
      word_count: this.countWords(deserialize(this.value, this.question)) ?? 0,
      editorConfig: {
        plugins: [
          Heading,
          Essentials,
          Bold,
          Italic,
          Underline,
          Strikethrough,
          Superscript,
          Font,
          Link,
          List,
          Indent,
          IndentBlock,
          Alignment,
          BlockQuote,
          Placeholder,
        ],
        toolbar: _.compact([
          'heading',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'superscript',
          '|',
          'fontFamily',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          'link',
          'blockQuote',
          '|',
          'alignment',
          'bulletedList',
          'numberedList',
          'indent',
          'outdent',
          '|',
          'undo',
          'redo',
        ]),
      },
    }
  },
  computed: {
    textarea() {
      return this.question.options && this.question.options.textarea ? this.question.options.textarea : false
    },
    isLocked() {
      if (this.question.options.locked && (!this.organization || !this.organization.auth_user)) return true
      return this.question.options.locked && !this.editing
    },
    isLockable() {
      return this.question.options.locked
    },
    isEditable() {
      return this.question.options.locked && !!this.organization && !!this.organization.auth_user
    },
    wordCountNum() {
      return parseInt(this.word_count ?? 0)
    },
    wordLimitNum() {
      return parseInt(this.wordLimit ?? 0)
    },
    isValid() {
      return !this.error && !((this.word_count > this.wordLimit) && this.wordLimitActive)
    }
  },
  methods: {
    countWords(text) {
      if (!text) return 0;
      const strippedText = text.replace(/<span style="[^"]*">/g, '').replace(/<[^>]+>/g, ' ');
      const withoutNbsp = strippedText.replace(/&nbsp;/g, ' ');
      const cleanText = withoutNbsp.replace(/\s+/g, ' ').trim();
      return cleanText ? cleanText.split(' ').length : 0;
    },
    validateWordCount() {
      if ((this.word_count > this.wordLimit) && this.wordLimitActive) {
        this.$emit('error', this.trans("question_max_word_overlimit"))
        this.error = this.trans("question_max_word_overlimit")
      } else {
        this.$emit('error', null)
        this.error = null
      }
    }
  },
  watch: {
    answer(newValue) {
      if (this.wordLimitActive) {
        this.word_count = this.countWords(newValue);
        this.validateWordCount();
      }
      this.$emit('input', this.answer)
    },
    value() {
      this.answer = deserialize(this.value, this.question)
    },
    question() {
      this.answer = deserialize(this.value, this.question)
    },
  },
  mounted() {
    this.validateWordCount()
    window['formQuestionInput'+(this.question.id||'')] = this
  }
}
</script>
